import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE = 'https://picketops.apper.so';
const TITLE = 'Fence Contractor Revenue OS for Quotes, Follow-Up & Jobs | PicketOps';
const DESCRIPTION = 'PicketOps helps fence contractors manage leads, build estimates, recover overdue quotes, schedule jobs, and keep production and payment records in one revenue workflow.';

const HOME_FAQ = [
  ['Who is PicketOps for?', 'PicketOps is business management and revenue workflow software built for fence contractors managing leads, estimates, proposals, follow-ups, jobs, production and payments.'],
  ['Does PicketOps help with estimate follow-up?', 'Yes. Contractors can assign follow-up dates to sent estimates, review due quotes, prepare customer drafts and keep communication history together.'],
  ['Can PicketOps price fence footage, gates and tear-out?', 'Yes. The price book supports per-foot fence rates plus gate and tear-out pricing for reusable estimating workflows.'],
  ['Does PicketOps have a free fence estimate calculator?', 'Yes. The public fence estimate calculator combines footage, per-foot selling rate, gates, tear-out, other charges and optional tax, then passes the result into Profit Guard for margin review.'],
  ['Does PicketOps have a contractor profit margin calculator?', 'Yes. PicketOps includes a free Profit Guard calculator for comparing selling price, entered direct costs, gross profit, gross margin, markup and a target-margin selling price.'],
  ['Does PicketOps automatically send email or SMS?', 'Not currently. Email and SMS entries are prepared as drafts for review; outbound messaging is not connected.'],
  ['How much does PicketOps cost?', 'The Founding Contractor Program offers a 30-day free trial and a $49 monthly founding price locked for 12 months. Operator activation is required after company setup. Support confirms trial dates and plan terms; signup does not start billing.'],
];

const PUBLIC_RESOURCES = {
  '/privacy': {
    title: 'Privacy Policy | PicketOps',
    description: 'How Syndicate Hub LLC handles PicketOps account information, workspace records, exports and privacy requests.',
    breadcrumb: 'Privacy policy',
    standalone: true,
  },
  '/terms': {
    title: 'Terms of Use | PicketOps',
    description: 'PicketOps service terms from Syndicate Hub LLC, covering account responsibilities, business records, tools and payment providers.',
    breadcrumb: 'Terms of use',
    standalone: true,
  },
  '/service-notice': {
    title: 'Service and Data-Use Notice | PicketOps',
    description: 'Understand PicketOps planning tools, saved workspace records, report sharing, payment evidence and provider setup limitations.',
    breadcrumb: 'Service and data-use notice',
    standalone: true,
  },
  '/fence-estimate-checklist': {
    title: 'Fence Estimate Checklist for Contractors | PicketOps',
    description: 'Use this free fence estimate checklist to review measurements, gates, site conditions, pricing assumptions and customer next steps before sending a quote.',
    breadcrumb: 'Fence estimate checklist',
  },
  '/contractor-revenue-scorecard': {
    title: 'Free Contractor Revenue Leak Scorecard | PicketOps',
    description: 'Score lead response, quote follow-up, pricing discipline, reviews and referrals, then get a practical action plan for the weakest part of your contractor revenue workflow.',
    breadcrumb: 'Contractor revenue scorecard',
  },
  '/fence-estimate-follow-up': {
    title: 'How to Follow Up on a Fence Estimate | 5-Touch Guide | PicketOps',
    description: 'Use a practical 1, 3, 7, 14 and 30-day fence estimate follow-up sequence with copy-ready scripts that keep quotes moving without sounding pushy.',
    breadcrumb: 'Fence estimate follow-up guide',
    howToSteps: [
      ['Day 1: Confirm receipt', 'Make sure the customer received the fence estimate and offer to clarify scope, materials or options.'],
      ['Day 3: Invite questions', 'Ask whether questions came up while the customer reviewed the estimate.'],
      ['Day 7: Check the timeline', 'Find out whether the project timing changed or another decision is holding it up.'],
      ['Day 14: Review the scope', 'Offer to walk through the scope so the customer can compare the work accurately.'],
      ['Day 30: Re-engage', 'Reconnect and ask whether the project is still active or the customer’s plans changed.'],
    ],
  },
  '/fence-profit-margin-calculator': {
    title: 'Fence Profit Margin Calculator | Free Contractor Tool | PicketOps',
    description: 'Use the free PicketOps Profit Guard calculator to compare fence job selling price, direct costs, gross profit, gross margin, markup and target-margin pricing.',
    breadcrumb: 'Fence profit margin calculator',
    calculator: true,
    faq: [
      ['What is gross margin on a fence job?', 'Gross margin is gross profit divided by the selling price, based on the direct costs entered into the calculator.'],
      ['Is markup the same as margin?', 'No. Markup compares gross profit to cost, while margin compares gross profit to selling price.'],
      ['What costs should I include?', 'Use the direct job costs you want to evaluate consistently, such as materials, labor, equipment, disposal, subcontractors or other job-specific costs.'],
      ['What is a good profit margin for a fence contractor?', 'There is no universal target. Cost structure, overhead, market, warranty exposure and business goals all affect the appropriate target.'],
    ],
  },
  '/fence-contractor-software': {
    title: 'Fence Contractor Software for Estimates, Follow-Up & Scheduling | PicketOps',
    description: 'Learn what to look for in fence contractor software for leads, estimating, quote follow-up, scheduling, production and billing, then see how PicketOps connects the workflow.',
    breadcrumb: 'Fence contractor software',
    faq: [
      ['What is fence contractor software?', 'Fence contractor software is a business workflow system for managing customers, estimates, proposals, follow-up, scheduling, production and billing around fence installation work.'],
      ['What should a small fence company look for first?', 'Start with the stages that cause the most rework or missed revenue, often lead response, repeatable estimating, quote follow-up and the handoff from accepted estimate to scheduled job.'],
      ['Does PicketOps replace accounting software?', 'No. PicketOps tracks job-related invoice, balance and payment records inside the contractor workflow, but it is not positioned as a full accounting ledger or tax system.'],
      ['Does PicketOps send customer text messages automatically?', 'Not currently. Email and SMS entries are prepared as drafts for contractor review; outbound messaging is not connected.'],
      ['Can PicketOps work for a one-person fence company?', 'Yes. The workflow is designed to be useful before a contractor has a large office staff.'],
    ],
  },
  '/fence-estimate-calculator': {
    title: 'Fence Estimate Calculator | Free Footage, Gate & Tear-Out Tool | PicketOps',
    description: 'Use the free PicketOps fence estimate calculator to price fence footage, gates, tear-out, other charges and optional tax, then send the total into Profit Guard.',
    breadcrumb: 'Fence estimate calculator',
    calculator: true,
    calculatorName: 'PicketOps Fence Estimate Calculator',
    faq: [
      ['How do I calculate a basic fence estimate?', 'Multiply fence length by your per-foot selling rate, then add gate pricing, tear-out pricing and other job-specific charges you choose to include.'],
      ['Should labor be included in the per-foot rate?', 'That depends on how you maintain your price book. Some contractors include labor and material in the per-foot selling rate while others separate them.'],
      ['Does this calculator calculate profit?', 'No. It estimates a selling total from your rates and quantities. Use Profit Guard afterward to compare the selling price with direct job costs.'],
      ['Does this create a PicketOps estimate?', 'No. The public calculator does not save customer data or create records.'],
    ],
  },
  '/fence-estimate-template': {
    title: 'Fence Estimate Template & Scope Builder | Free Contractor Tool | PicketOps',
    description: 'Build a copy-ready fence estimate scope from footage, material, height, gates, tear-out, inclusions, exclusions and site notes with this free PicketOps tool.',
    breadcrumb: 'Fence estimate scope builder',
    calculator: true,
    calculatorName: 'PicketOps Fence Estimate Scope Builder',
    faq: [
      ['What should a fence estimate scope include?', 'Include the fence type, approximate footage, height, gates, removal work, major inclusions, exclusions and site conditions or customer decisions that affect the work.'],
      ['Is this a contract template?', 'No. The builder creates a planning and proposal-scope summary and does not replace contract terms appropriate for your business and jurisdiction.'],
      ['Does the scope builder calculate a price?', 'No. Use the Fence Estimate Calculator for selling-price math and Profit Guard for direct-cost and gross-margin review.'],
      ['Does PicketOps save what I enter here?', 'No. The public builder does not create or update customer, estimate, proposal or job records.'],
    ],
  },
  '/fence-final-walkthrough-checklist': {
    title: 'Fence Final Walkthrough Checklist & Closeout Planner | PicketOps',
    description: 'Use a fence final walkthrough checklist for workmanship, gates, cleanup, punch-list items, customer handoff, warranty notes and final billing readiness.',
    breadcrumb: 'Fence final walkthrough checklist',
    calculator: true,
    calculatorName: 'PicketOps Fence Final Walkthrough and Closeout Planner',
    faq: [
      ['What should be on a fence final walkthrough checklist?', 'Review visible workmanship, gates and hardware, cleanup, final scope, customer questions, care or warranty information, punch-list items and final billing readiness.'],
      ['What is a punch list?', 'A punch list records remaining items that need correction, completion or confirmation before the project is treated as fully closed out.'],
      ['Does this certify code compliance or workmanship?', 'No. It is a workflow aid and does not inspect, certify or guarantee structural, code, permit, survey or warranty compliance.'],
      ['Does this save closeout records to PicketOps?', 'No. The public planner is session-only; saved operational records remain in Production, Jobs, Money and Follow-up.'],
    ],
  },
  '/fence-change-order-planner': {
    title: 'Fence Change Order Template & Planner | Contractor Tool | PicketOps',
    description: 'Plan fence additions and credits, revised contract value, internal direct-cost impact, schedule changes and a customer-ready change order summary.',
    breadcrumb: 'Fence change order planner',
    calculator: true,
    calculatorName: 'PicketOps Fence Change Order Planner',
    faq: [
      ['What should a fence change order include?', 'Document the changed scope, customer price adjustment, revised contract total, schedule impact and the approval terms your business uses.'],
      ['Why is direct cost excluded from the customer summary?', 'Direct cost and gross-profit impact are contractor-only planning information and are intentionally omitted from the copied customer summary.'],
      ['Can I enter credits for removed scope?', 'Yes. Credits reduce the customer contract value, avoided direct cost and planned workdays based on the positive values you enter.'],
      ['Does this create a legally binding change order?', 'No. It creates a planning and communication summary and does not replace your contract language, signatures or legal requirements.'],
    ],
  },
  '/fence-install-schedule-planner': {
    title: 'Fence Installation Schedule Planner | Start & Finish Dates | PicketOps',
    description: 'Turn fence crew workdays into a planned installation start, production finish and buffered finish using your chosen workweek and contingency days.',
    breadcrumb: 'Fence installation schedule planner',
    calculator: true,
    calculatorName: 'PicketOps Fence Installation Schedule Planner',
    faq: [
      ['How is the fence finish date calculated?', 'The planner counts only the working days in the workweek you select, starting from the requested date or the next available working day.'],
      ['Does it account for weather automatically?', 'No. Add contingency workdays if you want a buffer for weather or other delays.'],
      ['What should I use for installation days?', 'Use the whole scheduled workdays from your own labor plan or another duration you have estimated for the project.'],
      ['Does this update a PicketOps job schedule?', 'No. The public planner creates a planning window; actual install dates are saved inside Jobs & install planning.'],
    ],
  },
  '/fence-labor-crew-planner': {
    title: 'Fence Labor & Crew Planner | Crew Days and Labor Cost | PicketOps',
    description: 'Plan fence crew-days, scheduled workdays, worker-hours and optional direct labor cost from your own footage, crew size and production assumptions.',
    breadcrumb: 'Fence labor and crew planner',
    calculator: true,
    calculatorName: 'PicketOps Fence Labor and Crew Planner',
    faq: [
      ['What does feet per crew-day mean?', 'It is the fence footage you expect the whole crew to install in one workday for this specific scope.'],
      ['Why are there two labor-cost views?', 'Production-hour cost uses fractional crew-days, while full-day cost rounds installation to whole scheduled workdays.'],
      ['Does this include travel or callbacks?', 'Only if you enter those hours as additional direct labor. Other overhead and risk are not added automatically.'],
      ['Can I send labor cost into Profit Guard?', 'Yes. When an hourly direct-labor cost is entered, either labor-cost view can be passed into Profit Guard.'],
    ],
  },
  '/fence-measurement-worksheet': {
    title: 'Fence Measurement Worksheet | Free Contractor Field Tool | PicketOps',
    description: 'Break a fence project into measured runs, total linear footage, track heights, gates, tear-out and site notes, then send the totals into estimating tools.',
    breadcrumb: 'Fence measurement worksheet',
    calculator: true,
    calculatorName: 'PicketOps Fence Measurement Worksheet',
    faq: [
      ['How should I measure a fence job?', 'Break the layout into individual runs, record each run length and height, and keep gates, removal quantities and site conditions visible separately.'],
      ['Does this replace a site plan or survey?', 'No. It is a field-planning worksheet and does not replace surveys, utility locating, permits or property-line verification.'],
      ['Can I send measurements into other PicketOps tools?', 'Yes. The worksheet can pass footage, gate count, tear-out quantity and material into Material Takeoff, Estimate Calculator and Scope Builder.'],
      ['Are measurements saved to PicketOps?', 'No. The public worksheet is not saved to customer or estimate records unless you later create records inside the authenticated workspace.'],
    ],
  },
  '/fence-material-takeoff-calculator': {
    title: 'Fence Material Takeoff Calculator | Posts, Rails & Pickets | PicketOps',
    description: 'Estimate fence sections, posts, rails, pickets and optional concrete quantities from your own fence length, spacing, board dimensions and waste allowance.',
    breadcrumb: 'Fence material takeoff calculator',
    calculator: true,
    calculatorName: 'PicketOps Fence Material Takeoff Calculator',
    faq: [
      ['How do I estimate the number of fence posts?', 'Enter total fence length and your planned maximum post spacing. The calculator rounds up to whole sections, adds one closing post and includes any extra posts you enter.'],
      ['How does the picket count work?', 'Enter picket width and gap in inches, then the calculator applies the waste percentage you enter.'],
      ['Does this calculate structural requirements?', 'No. It is a quantity-planning tool and does not determine post depth, footing size, engineering or local code requirements.'],
      ['Are spacing or waste values recommendations?', 'No. Enter the spacing, dimensions and allowances appropriate for your own fence system and project.'],
    ],
  },
  '/fence-proposal-option-builder': {
    title: 'Fence Proposal Option Builder | Good Better Best Tool | PicketOps',
    description: 'Build clear Good, Better and Best fence proposal options with your own materials, scope descriptions, selling prices, recommended choice and deposit percentage.',
    breadcrumb: 'Fence proposal option builder',
    calculator: true,
    calculatorName: 'PicketOps Fence Proposal Option Builder',
    faq: [
      ['What is a Good Better Best proposal?', 'It presents multiple clearly differentiated choices instead of forcing every customer into one scope.'],
      ['Should the middle option always be recommended?', 'No. Recommend the option that best fits the actual project and customer needs.'],
      ['Does this tool suggest prices?', 'No. Enter the selling prices you intend to present and use Profit Guard to review direct costs and gross margin.'],
      ['Does this create a customer proposal?', 'No. The public builder creates a copy-ready comparison; saved proposal records are created inside the authenticated PicketOps workflow.'],
    ],
  },
  '/fence-contractor-tools': {
    title: 'Free Fence Contractor Tools | Estimating, Scheduling & Closeout | PicketOps',
    description: 'Use free PicketOps tools for fence measurements, takeoff, estimating, labor, scheduling, change orders, final walkthrough, margin checks and quote follow-up.',
    breadcrumb: 'Free fence contractor tools',
    collection: true,
    collectionItems: [
      ['Contractor Revenue Scorecard', '/contractor-revenue-scorecard'],
      ['Fence Measurement Worksheet', '/fence-measurement-worksheet'],
      ['Fence Material Takeoff Calculator', '/fence-material-takeoff-calculator'],
      ['Fence Estimate Calculator', '/fence-estimate-calculator'],
      ['Fence Estimate Scope Builder', '/fence-estimate-template'],
      ['Fence Labor & Crew Planner', '/fence-labor-crew-planner'],
      ['Fence Installation Schedule Planner', '/fence-install-schedule-planner'],
      ['Fence Change Order Planner', '/fence-change-order-planner'],
      ['Fence Final Walkthrough & Closeout Planner', '/fence-final-walkthrough-checklist'],
      ['Fence Proposal Option Builder', '/fence-proposal-option-builder'],
      ['Fence Profit Margin Calculator', '/fence-profit-margin-calculator'],
      ['Fence Estimate Checklist', '/fence-estimate-checklist'],
      ['Fence Estimate Follow-Up Guide', '/fence-estimate-follow-up'],
    ],
    faq: [
      ['Are the PicketOps contractor tools free?', 'Yes. The public tools can be used without creating a PicketOps account.'],
      ['Do the calculators use market prices?', 'No. The estimate calculator is designed for your own rates, and Profit Guard uses only the values you enter.'],
      ['Do these tools save customer information?', 'No. The public tools do not create customer, estimate, job or payment records.'],
      ['Which tool should I start with?', 'Start with the Contractor Revenue Scorecard if you are unsure, or the Fence Estimate Calculator if you already have a job to price.'],
    ],
  },
};

const PAGE_NAMES = {
  '/dashboard': 'Dashboard',
  '/leads': 'Leads',
  '/estimates': 'Estimates',
  '/proposals': 'Proposals',
  '/jobs': 'Jobs',
  '/follow-up': 'Follow-up',
  '/money': 'Money',
  '/production': 'Production',
  '/price-book': 'Price book',
  '/account': 'Account',
  '/payment-setup': 'Payment setup',
};

const PRODUCT_GRAPH = [
  {
    '@type': 'Organization',
    '@id': SITE + '/#organization',
    name: 'PicketOps',
    url: SITE + '/',
  },
  {
    '@type': 'WebSite',
    '@id': SITE + '/#website',
    name: 'PicketOps',
    url: SITE + '/',
    publisher: { '@id': SITE + '/#organization' },
  },
  {
    '@type': 'SoftwareApplication',
    '@id': SITE + '/#software',
    name: 'PicketOps',
    url: SITE + '/',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web browser',
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Fence contractors',
    },
    featureList: [
      'Fence contractor lead management',
      'Fence estimating and price book',
      'Fence measurement worksheet and material takeoff calculator',
      'Fence labor and crew planning',
      'Fence installation schedule planning',
      'Fence change order planning',
      'Fence final walkthrough, punch list and closeout planning',
      'Fence estimate scope and proposal option builders',
      'Profit Guard gross margin and markup calculator',
      'Quote follow-up and Quote Rescue workflow',
      'Proposal workflow',
      'Crew and install scheduling',
      'Production and work-order tracking',
      'Invoice and payment records',
    ],
    description: DESCRIPTION,
    offers: {
      '@type': 'Offer',
      price: '49',
      priceCurrency: 'USD',
      url: SITE + '/#pricing',
      description: 'Founding Contractor Program: operator activation required after company setup. Offer: 30 days free, then $49/month, with a 12-month founding price lock. Support confirms dates and terms; signup does not start billing.',
    },
  },
];

function homeStructuredData() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': SITE + '/#page',
        name: TITLE,
        description: DESCRIPTION,
        url: SITE + '/',
        dateModified: '2026-10-05',
        inLanguage: 'en-US',
        isPartOf: { '@id': SITE + '/#website' },
        mainEntity: { '@id': SITE + '/#software' },
        hasPart: [
          { '@id': SITE + '/#faq' },
          { '@id': SITE + '/#free-tools' },
        ],
      },
      ...PRODUCT_GRAPH,
      {
        '@type': 'ItemList',
        '@id': SITE + '/#free-tools',
        name: 'Free fence contractor tools',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Contractor Revenue Scorecard', url: SITE + '/contractor-revenue-scorecard' },
          { '@type': 'ListItem', position: 2, name: 'Fence Measurement Worksheet', url: SITE + '/fence-measurement-worksheet' },
          { '@type': 'ListItem', position: 3, name: 'Fence Material Takeoff Calculator', url: SITE + '/fence-material-takeoff-calculator' },
          { '@type': 'ListItem', position: 4, name: 'Fence Estimate Calculator', url: SITE + '/fence-estimate-calculator' },
          { '@type': 'ListItem', position: 5, name: 'Fence Estimate Scope Builder', url: SITE + '/fence-estimate-template' },
          { '@type': 'ListItem', position: 6, name: 'Fence Labor & Crew Planner', url: SITE + '/fence-labor-crew-planner' },
          { '@type': 'ListItem', position: 7, name: 'Fence Installation Schedule Planner', url: SITE + '/fence-install-schedule-planner' },
          { '@type': 'ListItem', position: 8, name: 'Fence Change Order Planner', url: SITE + '/fence-change-order-planner' },
          { '@type': 'ListItem', position: 9, name: 'Fence Final Walkthrough & Closeout Planner', url: SITE + '/fence-final-walkthrough-checklist' },
          { '@type': 'ListItem', position: 10, name: 'Fence Proposal Option Builder', url: SITE + '/fence-proposal-option-builder' },
          { '@type': 'ListItem', position: 11, name: 'Fence Profit Margin Calculator', url: SITE + '/fence-profit-margin-calculator' },
          { '@type': 'ListItem', position: 12, name: 'Fence Estimate Follow-Up Guide', url: SITE + '/fence-estimate-follow-up' },
          { '@type': 'ListItem', position: 13, name: 'Fence Estimate Checklist', url: SITE + '/fence-estimate-checklist' },
        ],
      },
      {
        '@type': 'FAQPage',
        '@id': SITE + '/#faq',
        mainEntity: HOME_FAQ.map(([question, answer]) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: answer,
          },
        })),
      },
    ],
  };
}

function resourceStructuredData(resource, canonical, title, description) {
  const extra = [
    ...(resource.collectionItems ? [{
      '@type': 'ItemList',
      '@id': canonical + '#tools',
      name: 'Free fence contractor tools',
      itemListElement: resource.collectionItems.map(([name, path], index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name,
        url: SITE + path,
      })),
    }] : []),
    ...(resource.howToSteps ? [{
      '@type': 'HowTo',
      '@id': canonical + '#howto',
      name: 'Fence estimate follow-up sequence',
      description,
      totalTime: 'P30D',
      step: resource.howToSteps.map(([name, text], index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name,
        text,
      })),
    }] : []),
    ...(resource.calculator ? [{
      '@type': 'WebApplication',
      '@id': canonical + '#calculator',
      name: resource.calculatorName || 'PicketOps Profit Guard',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web browser',
      description,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    }] : []),
    ...(resource.faq ? [{
      '@type': 'FAQPage',
      '@id': canonical + '#faq',
      mainEntity: resource.faq.map(([question, answer]) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: { '@type': 'Answer', text: answer },
      })),
    }] : []),
  ];

  const mainEntity = resource.collectionItems
    ? { '@id': canonical + '#tools' }
    : resource.howToSteps
      ? { '@id': canonical + '#howto' }
      : resource.calculator
        ? { '@id': canonical + '#calculator' }
        : undefined;
  const isToolChild = !resource.standalone && canonical !== SITE + '/fence-contractor-tools' && canonical !== SITE + '/fence-contractor-software';
  const breadcrumbItems = isToolChild
    ? [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE + '/' },
        { '@type': 'ListItem', position: 2, name: 'Free fence contractor tools', item: SITE + '/fence-contractor-tools' },
        { '@type': 'ListItem', position: 3, name: resource.breadcrumb, item: canonical },
      ]
    : [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE + '/' },
        { '@type': 'ListItem', position: 2, name: resource.breadcrumb, item: canonical },
      ];

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': resource.collection ? 'CollectionPage' : 'WebPage',
        '@id': canonical + '#page',
        name: title,
        description,
        url: canonical,
        isPartOf: { '@id': SITE + '/#website' },
        breadcrumb: { '@id': canonical + '#breadcrumb' },
        ...(mainEntity ? { mainEntity } : {}),
        dateModified: resource.standalone ? '2026-10-10' : '2026-10-05',
        inLanguage: 'en-US',
        ...(resource.faq ? { hasPart: { '@id': canonical + '#faq' } } : {}),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': canonical + '#breadcrumb',
        itemListElement: breadcrumbItems,
      },
      ...extra,
      ...PRODUCT_GRAPH.filter(node => ['Organization', 'WebSite'].includes(node['@type'])),
    ],
  };
}

export default function SearchMetadata({ publicSite = false }) {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPath = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
    const home = publicSite && normalizedPath === '/';
    const resource = publicSite ? PUBLIC_RESOURCES[normalizedPath] : null;
    const publicPage = home || Boolean(resource);
    const canonical = SITE + (home ? '/' : normalizedPath);
    const description = resource?.description || DESCRIPTION;
    const indexable = publicPage && window.location.origin === SITE;
    const title = resource?.title
      || (home ? TITLE : (PAGE_NAMES[pathname] || 'Contractor account') + ' | PicketOps');

    const previousTitle = document.title;
    const restore = [];

    const setTag = (selector, tag, attributes) => {
      let element = document.head.querySelector(selector);
      const created = !element;
      if (!element) {
        element = document.createElement(tag);
        document.head.appendChild(element);
      }
      const previous = Object.fromEntries(
        Object.keys(attributes).map(key => [key, element.getAttribute(key)])
      );
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
      restore.push(() => {
        if (created) element.remove();
        else {
          Object.entries(previous).forEach(([key, value]) => {
            if (value === null) element.removeAttribute(key);
            else element.setAttribute(key, value);
          });
        }
      });
    };

    document.title = title;
    setTag('meta[name="description"]', 'meta', {
      name: 'description',
      content: publicPage ? description : 'Sign in to manage your PicketOps fence contractor revenue workspace.',
    });
    setTag('meta[name="robots"]', 'meta', {
      name: 'robots',
      content: indexable ? 'index, follow, max-image-preview:large' : 'noindex, follow',
    });

    if (publicPage) {
      setTag('link[rel="canonical"]', 'link', { rel: 'canonical', href: canonical });

      [
        ['og:type', resource?.howToSteps ? 'article' : 'website'],
        ['og:site_name', 'PicketOps'],
        ['og:title', title],
        ['og:description', description],
        ['og:url', canonical],
      ].forEach(([property, content]) => {
        setTag('meta[property="' + property + '"]', 'meta', { property, content });
      });

      [
        ['twitter:card', 'summary'],
        ['twitter:title', title],
        ['twitter:description', description],
      ].forEach(([name, content]) => {
        setTag('meta[name="' + name + '"]', 'meta', { name, content });
      });

      const structured = document.createElement('script');
      structured.type = 'application/ld+json';
      structured.dataset.picketopsSeo = 'true';
      structured.textContent = JSON.stringify(
        resource
          ? resourceStructuredData(resource, canonical, title, description)
          : homeStructuredData()
      );
      document.head.appendChild(structured);
      restore.push(() => structured.remove());
    }

    return () => {
      document.title = previousTitle;
      restore.reverse().forEach(cleanup => cleanup());
    };
  }, [pathname, publicSite]);

  return null;
}
